import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Save, Eye, ArrowLeft } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

export default function LegalEditor() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState('tos');
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [pdfPath, setPdfPath] = useState('');
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadDocument();
  }, [user, selectedDoc]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/public/legal/document/${selectedDoc}`
      );
      const doc = response.data;
      
      if (!doc.placeholder) {
        setTitle(doc.title);
        setContentHtml(doc.content_html);
        setPdfPath(doc.pdf_storage_path || '');
      } else {
        // Set defaults for new docs
        setTitle(selectedDoc === 'tos' ? 'Terms of Service' : 'Privacy Policy');
        setContentHtml('');
        setPdfPath('');
      }
    } catch (err) {
      console.error('Error loading document:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title || !contentHtml) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/legal/documents`,
        {
          doc_type: selectedDoc,
          title,
          content_html: contentHtml,
          pdf_storage_path: pdfPath || null
        },
        { withCredentials: true, params: { doc_type: selectedDoc, title, content_html: contentHtml, pdf_storage_path: pdfPath || null } }
      );
      toast.success('Document saved successfully');
    } catch (err) {
      console.error('Error saving document:', err);
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#121417]">
      {/* Header */}
      <div className="p-6 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/internal/legal')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vault
          </button>
          
          <select
            value={selectedDoc}
            onChange={(e) => setSelectedDoc(e.target.value)}
            className="px-4 py-2 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
          >
            <option value="tos">Terms of Service</option>
            <option value="privacy">Privacy Policy</option>
          </select>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreview(!preview)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {preview ? 'Edit' : 'Preview'}
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {preview ? (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl font-bold text-white mb-6">{title}</h1>
              <div
                className="prose prose-invert prose-cyan max-w-none"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Document Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Terms of Service"
                  className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
                />
              </div>

              {/* PDF Path */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  PDF Storage Path (Dropbox)
                </label>
                <input
                  type="text"
                  value={pdfPath}
                  onChange={(e) => setPdfPath(e.target.value)}
                  placeholder="/Apps/ConsistencyApp/Legal/Terms_of_Service_Consistency.pdf"
                  className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-600"
                />
              </div>

              {/* Content HTML */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Content (HTML)
                </label>
                <textarea
                  value={contentHtml}
                  onChange={(e) => setContentHtml(e.target.value)}
                  placeholder="<h1>Title</h1><p>Content...</p>"
                  rows={20}
                  className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-600 resize-none"
                />
              </div>

              {/* LLC Notice Reminder */}
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-300">
                  📝 <strong>Remember to include:</strong> LLC Transfer Notice in all legal documents
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
